import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2,
  Package,
  DollarSign,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SupplementsPage = () => {
  const { t } = useLanguage();
  const [supplements, setSupplements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedSupplement, setSelectedSupplement] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    stock: '',
    category: ''
  });

  useEffect(() => {
    fetchSupplements();
  }, []);

  const fetchSupplements = async () => {
    try {
      const response = await axios.get(`${API}/supplements`);
      setSupplements(response.data);
    } catch (error) {
      console.error('Error fetching supplements:', error);
      toast.error('Error al cargar suplementos');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/supplements`, {
        ...formData,
        price: parseFloat(formData.price),
        stock: parseInt(formData.stock)
      });
      toast.success(t('savedSuccessfully'));
      setShowCreateModal(false);
      resetForm();
      fetchSupplements();
    } catch (error) {
      console.error('Error creating supplement:', error);
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/supplements/${selectedSupplement.id}`, {
        ...formData,
        price: parseFloat(formData.price),
        stock: parseInt(formData.stock)
      });
      toast.success(t('savedSuccessfully'));
      setShowEditModal(false);
      fetchSupplements();
    } catch (error) {
      console.error('Error updating supplement:', error);
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/supplements/${selectedSupplement.id}`);
      toast.success(t('deletedSuccessfully'));
      setShowDeleteDialog(false);
      setSelectedSupplement(null);
      fetchSupplements();
    } catch (error) {
      console.error('Error deleting supplement:', error);
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const openEditModal = (supplement) => {
    setSelectedSupplement(supplement);
    setFormData({
      name: supplement.name,
      description: supplement.description || '',
      price: supplement.price.toString(),
      stock: supplement.stock.toString(),
      category: supplement.category || ''
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({ name: '', description: '', price: '', stock: '', category: '' });
  };

  const getStockBadge = (stock) => {
    if (stock === 0) {
      return <Badge className="bg-red-100 text-red-700 border-red-200">{t('outOfStock')}</Badge>;
    }
    if (stock <= 10) {
      return <Badge className="bg-amber-100 text-amber-700 border-amber-200">{t('lowStock')}</Badge>;
    }
    return <Badge className="bg-green-100 text-green-700 border-green-200">{t('inStock')}</Badge>;
  };

  const filteredSupplements = supplements.filter(s =>
    s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (s.category && s.category.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#467322] border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="supplements-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold text-[#467322]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            {t('supplements')}
          </h1>
          <p className="text-gray-600 mt-1">{t('supplementsList')}</p>
        </div>
        <Button 
          onClick={() => {
            resetForm();
            setShowCreateModal(true);
          }}
          className="bg-[#467322] hover:bg-[#3A601C] text-white"
          data-testid="create-supplement-btn"
        >
          <Plus size={20} className="mr-2" />
          {t('newSupplement')}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-white border border-[#467322]/20">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-[#467322]/10 rounded-lg">
              <Package className="text-[#467322]" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Productos</p>
              <p className="text-2xl font-bold text-black">{supplements.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border border-[#467322]/20">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-amber-100 rounded-lg">
              <AlertTriangle className="text-amber-600" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-600">Stock Bajo</p>
              <p className="text-2xl font-bold text-black">
                {supplements.filter(s => s.stock > 0 && s.stock <= 10).length}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border border-[#467322]/20">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-red-100 rounded-lg">
              <Package className="text-red-600" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-600">Agotados</p>
              <p className="text-2xl font-bold text-black">
                {supplements.filter(s => s.stock === 0).length}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card className="bg-white border border-[#467322]/20">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#467322]" size={20} />
            <Input
              placeholder={`${t('search')}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 border-[#467322]/30 focus:border-[#467322]"
              data-testid="search-supplements"
            />
          </div>
        </CardContent>
      </Card>

      {/* Supplements Table */}
      <Card className="bg-white border border-[#467322]/20">
        <CardHeader>
          <CardTitle className="text-[#467322] flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            <Package size={24} />
            {t('supplementsList')} ({filteredSupplements.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredSupplements.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Package size={48} className="mx-auto mb-4 opacity-50" />
              <p>{t('noSupplements')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table className="table-striped">
                <TableHeader>
                  <TableRow className="bg-[#467322]">
                    <TableHead className="text-white font-semibold">{t('name')}</TableHead>
                    <TableHead className="text-white font-semibold">{t('category')}</TableHead>
                    <TableHead className="text-white font-semibold">{t('price')}</TableHead>
                    <TableHead className="text-white font-semibold">{t('stock')}</TableHead>
                    <TableHead className="text-white font-semibold">{t('status')}</TableHead>
                    <TableHead className="text-white font-semibold text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSupplements.map((supplement) => (
                    <TableRow key={supplement.id} className="hover:bg-[#F2F7F0]">
                      <TableCell>
                        <div>
                          <p className="font-medium">{supplement.name}</p>
                          {supplement.description && (
                            <p className="text-sm text-gray-500 truncate max-w-xs">{supplement.description}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{supplement.category || '-'}</TableCell>
                      <TableCell className="font-semibold text-[#467322]">
                        ${supplement.price.toFixed(2)}
                      </TableCell>
                      <TableCell>{supplement.stock}</TableCell>
                      <TableCell>{getStockBadge(supplement.stock)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditModal(supplement)}
                            className="text-[#467322] hover:bg-[#F2F7F0]"
                            data-testid={`edit-supplement-${supplement.id}`}
                          >
                            <Edit size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedSupplement(supplement);
                              setShowDeleteDialog(true);
                            }}
                            className="text-red-500 hover:bg-red-50"
                            data-testid={`delete-supplement-${supplement.id}`}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-white border border-[#467322]/20 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl text-[#467322]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              {t('newSupplement')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-black">{t('name')} *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="border-[#467322]/30 focus:border-[#467322]"
                data-testid="input-supplement-name"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-black">{t('description')}</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="border-[#467322]/30 focus:border-[#467322]"
                data-testid="input-supplement-description"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-black">{t('price')} *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  required
                  className="border-[#467322]/30 focus:border-[#467322]"
                  data-testid="input-supplement-price"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-black">{t('stock')} *</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.stock}
                  onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                  required
                  className="border-[#467322]/30 focus:border-[#467322]"
                  data-testid="input-supplement-stock"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-black">{t('category')}</Label>
              <Input
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                placeholder="Ej: Proteínas, Vitaminas..."
                className="border-[#467322]/30 focus:border-[#467322]"
                data-testid="input-supplement-category"
              />
            </div>
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateModal(false)}
                className="border-[#467322] text-[#467322]"
              >
                {t('cancel')}
              </Button>
              <Button
                type="submit"
                className="bg-[#467322] hover:bg-[#3A601C] text-white"
                data-testid="save-supplement-btn"
              >
                {t('save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-white border border-[#467322]/20 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl text-[#467322]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              {t('edit')} {t('supplements')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-black">{t('name')} *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="border-[#467322]/30 focus:border-[#467322]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-black">{t('description')}</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="border-[#467322]/30 focus:border-[#467322]"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-black">{t('price')} *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  required
                  className="border-[#467322]/30 focus:border-[#467322]"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-black">{t('stock')} *</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.stock}
                  onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                  required
                  className="border-[#467322]/30 focus:border-[#467322]"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-black">{t('category')}</Label>
              <Input
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="border-[#467322]/30 focus:border-[#467322]"
              />
            </div>
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowEditModal(false)}
                className="border-[#467322] text-[#467322]"
              >
                {t('cancel')}
              </Button>
              <Button
                type="submit"
                className="bg-[#467322] hover:bg-[#3A601C] text-white"
              >
                {t('save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="bg-white border border-[#467322]/20">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#467322]">{t('confirmDelete')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('deleteWarning')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#467322] text-[#467322]">
              {t('cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SupplementsPage;
